import { c as _c } from "react/compiler-runtime";
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import type * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { useMapVisibleLocations } from '../../fog-of-war/useMapVisibleLocations';
import { createPinScreenIndex, findClosestPinInScreenIndex, getPinHitTestTransformKey, PIN_HIT_RADIUS_PX, type PinScreenIndex, type ScreenPoint, translateScreenPointToCachedTransform } from '../utils/pin-hit-testing';
interface CachedPinScreenIndex {
  readonly index: PinScreenIndex;
  readonly pins: readonly MapDataLocationFeature[];
  readonly transformKey: string;
  readonly anchor: [number, number];
  readonly anchorPoint: ScreenPoint;
}
export function useVisiblePinHitTester() {
  const $ = _c(4);
  const visiblePins = useMapVisibleLocations();
  const visiblePinsRef = useRef(visiblePins);
  const indexRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== visiblePins) {
    t0 = () => {
      visiblePinsRef.current = visiblePins;
      indexRef.current = null;
    };
    t1 = [visiblePins];
    $[0] = visiblePins;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (map, point, t3) => {
      const maxDistancePx = t3 === undefined ? PIN_HIT_RADIUS_PX : t3;
      const pins = visiblePinsRef.current;
      const transformKey = getPinHitTestTransformKey(map);
      let cached = indexRef.current;
      if (!cached || cached.pins !== pins || cached.transformKey !== transformKey) {
        const center = map.getCenter();
        const anchor = [center.lng, center.lat];
        cached = {
          index: createPinScreenIndex(map, pins),
          pins,
          transformKey,
          anchor,
          anchorPoint: map.project(anchor)
        };
        indexRef.current = cached;
      }
      const adjustedPoint = translateScreenPointToCachedTransform(point, map.project(cached.anchor), cached.anchorPoint);
      return findClosestPinInScreenIndex(cached.index, adjustedPoint, maxDistancePx);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}